import { AlertTriangle } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * @param {{
 *   summary: {
 *     total: number
 *     confirmed: number
 *     checkedIn: number
 *     inService: number
 *     waiting: number
 *     completed: number
 *     attention: number
 *   }
 *   unavailable?: boolean
 *   activeFilter?: string | null
 *   onFilter?: (key: string | null) => void
 * }} props
 */
export function AppointmentSummaryBar({
  summary,
  unavailable = false,
  activeFilter = null,
  onFilter,
}) {
  if (unavailable) {
    return (
      <div
        className="flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        <span className="font-semibold uppercase tracking-wider text-muted-foreground/80">Today</span>
        <span className="text-foreground/80">Schedule unavailable</span>
      </div>
    )
  }

  const chips = [
    {
      key: 'total',
      label: summary.total === 0 ? 'No bookings' : summary.total === 1 ? 'booking' : 'bookings',
      value: summary.total === 0 ? null : summary.total,
      tone: 'neutral',
      always: true,
    },
    { key: 'confirmed', label: 'Confirmed', value: summary.confirmed, tone: 'confirmed' },
    { key: 'checkedIn', label: 'Checked in', value: summary.checkedIn, tone: 'checked' },
    { key: 'inService', label: 'In service', value: summary.inService, tone: 'active' },
    { key: 'waiting', label: 'Waiting', value: summary.waiting, tone: 'waiting' },
    { key: 'completed', label: 'Done', value: summary.completed, tone: 'muted' },
  ]

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Today’s schedule summary"
    >
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Today
      </span>

      {chips.map((chip) => {
        if (!chip.always && (!chip.value || chip.value === 0)) {
          return null
        }

        const isActive = activeFilter === chip.key

        return (
          <button
            key={chip.key}
            type="button"
            onClick={() => onFilter?.(isActive ? null : chip.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              'border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
              isActive
                ? 'bg-primary/12 text-foreground ring-1 ring-primary/25'
                : 'bg-muted/45 text-muted-foreground hover:bg-muted/70 hover:text-foreground',
            )}
          >
            {chip.key !== 'total' ? (
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  chip.tone === 'confirmed' && 'bg-sky-500',
                  chip.tone === 'checked' && 'bg-amber-500',
                  chip.tone === 'active' && 'bg-[hsl(var(--primary))]',
                  chip.tone === 'waiting' && 'bg-violet-500',
                  chip.tone === 'muted' && 'bg-muted-foreground/45',
                )}
                aria-hidden
              />
            ) : null}
            {chip.value !== null ? (
              <span className="tabular-nums text-foreground">{chip.value}</span>
            ) : null}
            <span>{chip.label}</span>
          </button>
        )
      })}

      {summary.attention > 0 ? (
        <button
          type="button"
          onClick={() => onFilter?.(activeFilter === 'attention' ? null : 'attention')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
            activeFilter === 'attention'
              ? 'bg-amber-100 text-amber-950 ring-1 ring-amber-300/70'
              : 'bg-amber-50 text-amber-900 hover:bg-amber-100',
          )}
          aria-label={`${summary.attention} items need attention`}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden />
          <span className="tabular-nums">{summary.attention}</span>
        </button>
      ) : null}
    </div>
  )
}
